// /* eslint-disable @typescript-eslint/ban-types */
// /* eslint-disable functional/no-return-void */
import { useLazyQuery } from '@apollo/client';
import {
  Form as FForm,
  FieldAttributes,
  Formik,
  FormikHelpers,
  FormikValues,
  useField,
  useFormikContext,
} from 'formik';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Col,
  Form,
  InputGroup,
  Modal,
  ModalProps,
  OverlayTrigger,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FaCheck, FaTimes } from 'react-icons/fa';
import LoadingOverlay from '@achmadk/react-loading-overlay';
import { createModal } from 'react-modal-promise';

import {
  ButtonSearch,
  FieldInput,
  FieldLabel,
  FieldTooltip,
  FormRow,
  TablePaginatedV8,
} from '../index';

/*

Use As:

Δες Αφης fs-contributor.ts

*/

// -------------------------------------------------------------------------------
// Formik element
// -------------------------------------------------------------------------------

export type FFProps = {
  readonly as?: React.ElementType<any>;
  readonly label: string | React.ReactNode;
  readonly placeholder?: string;
  readonly disabled?: boolean;
  readonly required?: boolean;
  readonly type?: string;
  readonly size?: string;
  //// eslint-disable-next-line functional/no-return-void
  readonly onChange?: (valueId: string, valueName: string) => void;
} & FieldAttributes<any>;

export const FormFieldModalDlg = ({
  as = 'div',
  label = 'select label',
  idFieldName = 'id',
  nameFieldName = 'name',
  disabled = false,
  placeholder = '',
  required = false,
  hook,
  where,
  size,
  DlgComponent,
  onChange,
}: FFProps) => {
  const { t } = useTranslation();

  if (typeof label === 'string') label = t(label);

  const [{ value }, { touched, error, initialValue }] =
    useField<string>(idFieldName);
  const [{ value: nameValue }] = useField<string>(nameFieldName);

  const changed = value !== initialValue;
  const { setFieldValue, setFieldTouched, submitCount } =
    useFormikContext<any>();

  const setFieldValueAndTouched = useCallback(
    async (_value, touched: boolean) => {
      await setFieldValue(idFieldName, parseInt(_value), true); //field,value,shouldValidate
      await setFieldTouched(idFieldName, touched); //field,touched,shouldValidate
      if (hook) {
        console.log('hook if');

        if (_value.length > 5) {
          console.log('value if');
          hook({ variables: { where: where(_value) } });
        }
      }
    },
    [hook, idFieldName, setFieldTouched, setFieldValue, where]
  );
  //
  //-------------------- Handlers -------------------------------------------
  //
  const handleChange = useCallback(
    async (valueId: string, valueName: string) => {
      await setFieldValueAndTouched(valueId, true);
      await setFieldValue(nameFieldName, valueName, true); //field,value,shouldValidate

      if (onChange) onChange(valueId, valueName);
    },
    [setFieldValueAndTouched, onChange]
  );

  return (
    <Form.Group as={as} controlId={idFieldName} className="mb-2">
      <FieldLabel
        size={size}
        text={label}
        changed={changed}
        required={required}
      />
      <OverlayTrigger
        delay={{ show: 1000, hide: 200 }}
        overlay={
          disabled ? (
            <></>
          ) : (
            <FieldTooltip text={idFieldName} value={value || ''} />
          )
        }
        placement="top"
      >
        <div
          style={{
            pointerEvents: disabled ? 'none' : 'auto',
            opacity: disabled ? 0.5 : 1,
          }}
        >
          <InputGroup>
            <DlgComponent
              initValueId={value}
              initValueName={nameValue}
              onValueChange={handleChange}
              disabled={disabled}
            />
          </InputGroup>
        </div>
      </OverlayTrigger>
      {/* <FieldFeedback type="invalid" name={idFieldName} /> */}
      {(touched || submitCount > 0) && (
        <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
      )}
    </Form.Group>
  );
};
// -------------------------------------------------------------------------------
// element ModalSelect
// -------------------------------------------------------------------------------

type Props = {
  readonly open: boolean;
  readonly close: (result: any) => void;
  readonly title?: string;
  /** body of dialog */
  /** accept text */
  readonly acceptText?: string;
  /** deny text */
  readonly denyText?: string;
  readonly type?: string;
  readonly columns: any;
  readonly gqlQuery: any;
  readonly orderBy: string;
  readonly buildWhereFromFilters?: (values: object) => object;
  readonly Filters: (props: FilterProps) => JSX.Element;
  readonly initFilters?: any;
  readonly autoFetchData?: boolean; // Φέρνει δεδομένα απο την αρχή
  readonly appStore: any;
  readonly disabled: boolean;
} & ModalProps;

const ModalSelect = ({
  open,
  close,
  title = 'Επιλογή',
  acceptText = 'ΟΚ',
  type = 'text',
  columns: initColumns,
  gqlQuery,
  orderBy = 'name',
  buildWhereFromFilters = defaultBuildWhereFromFilters,
  Filters = DefaultFilters,
  initFilters = undefined,
  autoFetchData = false,
  appStore,
  idValues = '',
  size = 'lg',
  disabled = false,
}: Props) => {
  const { t, i18n } = useTranslation();

  // Translate Columns
  const columns = initColumns.map((value) => {
    return {
      ...value,
      header: t(value.header),
      columnName: t(value.columnName),
    };
  });

  return (
    <Modal size={size} show={open} centered backdrop="static">
      <Modal.Header style={{ backgroundColor: '#699ac0', color: 'white' }}>
        <Modal.Title>{t(title)}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <SelectRecordsForm
          onValueSet={(id) => close(id)}
          onClose={(id) => close(id)}
          columns={columns}
          gqlQuery={gqlQuery}
          orderBy={orderBy}
          buildWhereFromFilters={buildWhereFromFilters}
          Filters={Filters}
          initFilters={initFilters}
          autoFetchData={autoFetchData}
          appStore={appStore}
          disabled={disabled}
        />
      </Modal.Body>
    </Modal>
  );
};

// --------------------------------------------------------------------------------------------------- //
const SelectRecordsForm = ({
  onValueSet,
  onClose,
  columns,
  gqlQuery,
  orderBy,
  buildWhereFromFilters,
  Filters,
  initFilters,
  autoFetchData = false,
  appStore,
  disabled = false,
  //ids already selected
}) => {
  //
  const { t, i18n } = useTranslation();
  //
  const [selection, setSelection] = useState<any>(undefined);
  //
  const [initPageIndex, setInitPageIndex] = useState(0);
  const [queryParams, setQueryParams] = useState({
    offset: appStore.pageSize * initPageIndex,
    limit: appStore.pageSize,
    order: orderBy,
    where: '',
  });

  const [get, { data, loading }] = useLazyQuery(gqlQuery, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      // Επέλεξε το πρώτο
      if (!data || Object.keys(data).length === 0) return;
      const queryResult = data[Object.keys(data)[0]];
      if (queryResult.recordCount >= 1) {
        const rec = queryResult.data[0];
        // store selection
        //setSelection({ id: rec.id, name: rec.name });
        setSelection(rec);

        setTimeout(() => {
          // set focus to ok button
          const el = document.getElementById('okButton');
          if (el) el.focus();
        }, 300);
      }
    },
  });
  // use E
  useEffect(() => {
    // Auto Get Data
    // autoFetchData?: boolean; // Φέρνει δεδομένα απο την αρχή
    if (autoFetchData) {
      filtersChanged(initFilters, undefined);
    }
  }, []);

  const filtersChanged = async (
    values: FormikValues,
    formikHelpers: FormikHelpers<FormikValues>
  ) => {
    const where = buildWhereFromFilters(values);

    // Σύνθεσε τα vars του graphql
    const newQueryParams = {
      ...queryParams,
      offset: 0,
      where: JSON.stringify(where),
    };
    setQueryParams(newQueryParams); // αποθήκευσε τα vars
    await get({ variables: { ...newQueryParams } }); // φερε τα δεδομένα απο τον σερβερ
    setInitPageIndex(0); // αρχικοποίησε το gridTable στην 1η σελίδα
  };

  /* handle Pagination */
  const onGridPageChange = useCallback(
    (pageIndex, pageSize: number) => {
      console.log('gridPageChanged', pageIndex, pageSize);
      setInitPageIndex(pageIndex);
      const newQueryParams = {
        ...queryParams,
        offset: pageSize * pageIndex,
        limit: pageSize,
      };
      setQueryParams(newQueryParams);
      get({ variables: { ...newQueryParams } }); // φερε τα δεδομένα απο τον σερβερ
    },
    [get, queryParams]
  );

  /** Επιλογή γραμμής στο table-v8: το onRowClick δίνει { table, row } */
  const onTableRowClick = ({ table, row }) => {
    // Μόνο η τρέχουσα γραμμή επιλεγμένη
    table.toggleAllRowsSelected(false);
    row.toggleSelected(true);
    setSelection(row.original);
  };

  useEffect(() => {
    // Set Focus to filter input box
    const inputElement = document.getElementById('searchText');
    if (inputElement) inputElement.focus();
  }, []);

  return (
    <div>
      {/* Filters */}
      <Filters onChange={filtersChanged} initialValues={initFilters} />
      <span className="mt-1" />
      {/* Grid */}
      <LoadingOverlay
        active={loading}
        spinner
        text={t('Παρακαλώ περιμένετε...')}
      >
        {data ? (
          <TablePaginatedV8
            id="modal-select"
            appStore={appStore}
            // title={'Στοιχεία Υπόχρεων'}
            columns={columns}
            data={data[Object.keys(data)[0]]?.data}
            dataCount={data[Object.keys(data)[0]]?.recordCount}
            onPageChange={onGridPageChange}
            initPageIndex={initPageIndex}
            style={{
              minWidth: 'inherit !important',
              minHeight: '200px',
              // Περιορισμός ύψους ώστε το modal να χωράει στην οθόνη· scroll μόνο στον πίνακα
              maxHeight: 'calc(100vh - 280px)',
              overflowY: 'auto',
            }}
            rowProps={(row) =>
              row.getIsSelected() ? { className: 'table-warning' } : null
            }
            selectedRowIndex={selection ? 0 : null}
            onRowClick={onTableRowClick}
            initSorting={[{ desc: true, id: 'name' }]}
          />
        ) : (
          <div
            style={{
              height: '340px',
              background: 'rgba(0, 0, 0, 0.03)',
              borderRadius: '0.3rem',
            }}
          ></div>
        )}
      </LoadingOverlay>
      {/* Footer Buttons */}
      <div className="float-end mt-4 pt-3 mb-0">
        <Button
          className="me-3"
          variant="primary"
          size="sm"
          style={{ paddingTop: '4px', paddingBottom: '4px', minWidth: '7rem' }}
          onClick={() => onValueSet(selection)}
          disabled={!selection}
          id="okButton"
        >
          <FaCheck /> &nbsp;
          {i18n.language === 'gr' ? t('OK') : t('OK')}
        </Button>
        <Button
          variant="danger"
          size="sm"
          style={{ paddingTop: '4px', paddingBottom: '4px', minWidth: '7rem' }}
          onClick={() => onClose(false)}
        >
          <FaTimes /> &nbsp;
          {i18n.language === 'gr' ? t('Άκυρο') : t('Cancel')}
        </Button>
      </div>
    </div>
  );
};

// --------------------------------------------------------------------------------------------------- //
// Filters
//
const defaultBuildWhereFromFilters = (values) => {
  if (!values.searchText) return null;
  const where = { name: { $like: '%' + values.searchText + '%' } };
  return where;
};

// ----------- Filters ------------------ //
type OnChangeFunc = (
  values: FormikValues,
  formikHelpers: FormikHelpers<FormikValues>
) => void;
type FilterProps = {
  readonly onChange: OnChangeFunc;
  readonly initialValues?: any;
};
const DefaultFilters: (props: FilterProps) => JSX.Element = ({
  onChange,
  initialValues,
}) => {
  return (
    <Formik
      initialValues={initialValues ? initialValues : { searchText: '' }}
      onSubmit={onChange}
    >
      <Form as={FForm}>
        <FormRow className="mb-3 align-items-end">
          <Col sm={6} lg={8} xl={8} className="ps-0 pe-0">
            <FieldInput
              as={Col}
              name="searchText"
              id="searchText"
              label="Επωνυμία"
              type="search"
            />
          </Col>
          <Col className="ps-0 pe-0 me-3 ">
            <ButtonSearch size="lg" type="submit" className="float-end mb-2" />
          </Col>
        </FormRow>
      </Form>
    </Formik>
  );
};

// --------------------------------------------------------------------------------------------------- //
// exports
// --------------------------------------------------------------------------------------------------- //

const promiseModalSelect = createModal(ModalSelect);

export { promiseModalSelect };
export type { FilterProps, OnChangeFunc };
